/**
 * ShareFamilyVaultModal
 *
 * Modal that lets the client email their generated "My Family Should Know" PDF
 * to a chosen recipient. Recipient types: Nominee (Family Member), Financial
 * Consultant, CA, Lawyer, Other. Backend endpoint:
 *   POST /api/client/family-vault/share-pdf
 */
import React, { useState } from 'react';
import axios from 'axios';
import {
  FaTimes, FaSpinner, FaCheck, FaUserFriends, FaHandshake, FaBriefcase,
  FaBalanceScale, FaAddressCard, FaEnvelope, FaUser, FaShieldAlt
} from 'react-icons/fa';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const RECIPIENT_OPTIONS = [
  { id: 'nominee',    label: 'Nominee (Family Member)', Icon: FaUserFriends,   colorClass: 'text-emerald-600', ringClass: 'ring-emerald-500' },
  { id: 'consultant', label: 'Financial Consultant',    Icon: FaHandshake,     colorClass: 'text-[#B18A3A]',   ringClass: 'ring-[#D4A84F]' },
  { id: 'ca',         label: 'CA (Chartered Accountant)', Icon: FaBriefcase,   colorClass: 'text-sky-600',     ringClass: 'ring-sky-500' },
  { id: 'lawyer',     label: 'Lawyer',                  Icon: FaBalanceScale,  colorClass: 'text-purple-600',  ringClass: 'ring-purple-500' },
  { id: 'other',      label: 'Other (Trusted Contact)', Icon: FaAddressCard,   colorClass: 'text-slate-600',   ringClass: 'ring-slate-500' },
];

const ShareFamilyVaultModal = ({ generatePDF, onClose }) => {
  const [step, setStep] = useState('select'); // select | sending | done
  const [recipientType, setRecipientType] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const canSubmit =
    recipientType &&
    recipientName.trim().length > 1 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      setError('Please choose a recipient type and enter a valid name & email.');
      return;
    }
    setError('');
    setStep('sending');
    try {
      // Ask the parent's generatePDF for base64 payload (no download)
      const pdfResult = await generatePDF('share');
      if (!pdfResult?.base64) {
        throw new Error('Could not generate PDF. Please try again.');
      }
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/client/family-vault/share-pdf`,
        {
          recipient_type: recipientType,
          recipient_name: recipientName.trim(),
          recipient_email: recipientEmail.trim().toLowerCase(),
          note: note.trim() || null,
          pdf_base64: pdfResult.base64,
          pdf_filename: pdfResult.fileName,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(res.data?.message || 'Report shared successfully.');
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to send report');
      setStep('select');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="share-vault-modal">
      <div
        className="bg-white rounded-2xl border border-[#EDE9DE] max-w-lg w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#EDE9DE] bg-gradient-to-r from-[#0F4C5C] to-[#093039]">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#D4A84F] flex items-center justify-center">
              <FaEnvelope className="text-[#263238]" size={18} />
            </div>
            <div>
              <p className="font-poppins text-white text-lg font-semibold leading-tight">Share Report by Email</p>
              <p className="text-white/70 text-xs">Your &ldquo;My Family Should Know&rdquo; PDF</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="share-vault-close">
            <FaTimes size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-6 flex-1">
          {step === 'done' ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
                <FaCheck className="text-emerald-600" size={26} />
              </div>
              <p className="font-poppins text-[#0F4C5C] text-lg font-semibold mb-1">Report shared</p>
              <p className="text-[#4E5B65] text-sm">{success}</p>
              <p className="mt-4 text-xs text-[#78838C]">A confidential copy has been emailed. It will not be stored anywhere else.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="share-vault-form">
              {/* Step 1 — Choose recipient type */}
              <div>
                <p className="text-xs uppercase tracking-wide text-[#78838C] mb-2 font-semibold">1. Who are you sharing with?</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {RECIPIENT_OPTIONS.map((opt) => {
                    const active = recipientType === opt.id;
                    return (
                      <button
                        type="button"
                        key={opt.id}
                        onClick={() => setRecipientType(opt.id)}
                        data-testid={`share-recipient-${opt.id}`}
                        className={`flex items-center gap-2 p-3 rounded-lg border text-sm text-left transition-all ${
                          active
                            ? 'bg-[#F8F6F0] border-[#0F4C5C] shadow-sm ring-1 ring-[#0F4C5C]/25'
                            : 'bg-white border-[#EDE9DE] hover:border-[#0F4C5C]/40'
                        }`}
                      >
                        <opt.Icon className={opt.colorClass} />
                        <span className="text-[#263238] font-medium">{opt.label}</span>
                        {active && <FaCheck className="text-[#0F4C5C] ml-auto" size={12} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 2 — Name */}
              <div>
                <label className="text-xs uppercase tracking-wide text-[#78838C] mb-1 font-semibold flex items-center gap-1.5">
                  <FaUser size={10} /> 2. Their Name
                </label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="e.g. Priya Sharma"
                  data-testid="share-recipient-name"
                  className="w-full px-3 py-2 bg-[#F8F6F0] border border-[#EDE9DE] rounded-lg text-[#263238] focus:border-[#0F4C5C] focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/15 text-sm"
                />
              </div>

              {/* Step 3 — Email */}
              <div>
                <label className="text-xs uppercase tracking-wide text-[#78838C] mb-1 font-semibold flex items-center gap-1.5">
                  <FaEnvelope size={10} /> 3. Their Email
                </label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="e.g. priya@example.com"
                  data-testid="share-recipient-email"
                  className="w-full px-3 py-2 bg-[#F8F6F0] border border-[#EDE9DE] rounded-lg text-[#263238] focus:border-[#0F4C5C] focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/15 text-sm"
                />
              </div>

              {/* Step 4 — Optional note */}
              <div>
                <label className="text-xs uppercase tracking-wide text-[#78838C] mb-1 font-semibold">4. Personal note (optional)</label>
                <textarea
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Anything you'd like them to know..."
                  data-testid="share-recipient-note"
                  className="w-full px-3 py-2 bg-[#F8F6F0] border border-[#EDE9DE] rounded-lg text-[#263238] focus:border-[#0F4C5C] focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/15 text-sm resize-none"
                />
              </div>

              {/* Confidentiality warning */}
              <div className="p-3 bg-[#FBF6EA] border-l-4 border-[#D4A84F] rounded-lg text-xs text-[#846829] flex items-start gap-2">
                <FaShieldAlt className="mt-0.5 flex-shrink-0" />
                <span>Only share with people you fully trust — the PDF contains bank accounts, policies, and other confidential financial information.</span>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
              )}

              <button
                type="submit"
                disabled={!canSubmit || step === 'sending'}
                data-testid="share-vault-submit"
                className={`w-full py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                  !canSubmit || step === 'sending'
                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    : 'bg-[#0F4C5C] text-white hover:bg-[#093039] shadow-md shadow-[#0F4C5C]/25'
                }`}
              >
                {step === 'sending' ? (
                  <><FaSpinner className="animate-spin" /> Generating &amp; sending…</>
                ) : (
                  <><FaEnvelope /> Send Report</>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-[#EDE9DE] bg-[#F8F6F0] flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white border border-[#EDE9DE] text-[#4E5B65] text-sm font-medium rounded-lg hover:border-[#0F4C5C]/40"
            data-testid="share-vault-done-btn"
          >
            {step === 'done' ? 'Done' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareFamilyVaultModal;
